import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { stationRequests } from "@/lib/api/requests/stations";
import { cookingSessionKeys } from "@/hooks/cooking-sessions/keys";
import type {
  CreateStationPayload,
  ListStationsParams,
  UpdateStationPayload,
} from "@/types/station";

export const stationKeys = {
  all: ["stations"] as const,
  list: (params?: ListStationsParams) => [...stationKeys.all, "list", params ?? {}] as const,
};

/**
 * The stations of a location (YC-36), in the kitchen's own order. Read by
 * anyone on the cooking board: the assign picker and the filter are built from it.
 */
export function useStations(params?: ListStationsParams, enabled = true) {
  return useQuery({
    queryKey: stationKeys.list(params),
    queryFn: () => stationRequests.list(params),
    enabled,
    // Stations change when an administrator edits them, which is rare.
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateStations() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: stationKeys.all });
    // A renamed or deleted station is on the board's rows too.
    queryClient.invalidateQueries({ queryKey: cookingSessionKeys.all });
  };
}

export function useCreateStation() {
  const invalidate = useInvalidateStations();
  return useMutation({
    mutationFn: (data: CreateStationPayload) => stationRequests.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateStation() {
  const invalidate = useInvalidateStations();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateStationPayload }) =>
      stationRequests.update(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteStation() {
  const invalidate = useInvalidateStations();
  return useMutation({
    mutationFn: (id: number) => stationRequests.remove(id),
    onSuccess: invalidate,
  });
}
